import { DESTINATIONS, type Destination } from './navigation'
import './MobileTabBar.css'

/**
 * The bottom tab bar — what the header becomes on a phone.
 *
 * Fixed to the bottom rather than the top, which is the whole point: the top of
 * a phone screen is the hardest place to reach one-handed and the bottom is the
 * easiest, so the navigation you use constantly goes where your thumb already
 * is. The header's logo goes away entirely; on a 375px screen every pixel of
 * chrome is taken from the content, and you know which app you have open.
 *
 * The last item is Account — the avatar menu from the top-right, which has
 * nowhere else to go once the header is gone. It was four items until Internal
 * arrived on 13 Aug and made it five.
 */

const ICONS: Record<Destination, string> = {
  pro: '◆',
  app: '▤',
  marketing: '✎',
  // The design's own glyph for Internal, from `mobileIcons` in the prototype.
  internal: '☑',
}

export function MobileTabBar({
  active,
  onSelect,
  onAccount,
  accountActive,
  accountPhoto,
  marketingBadge,
}: {
  /** The lit destination, or null while the account sheet is open. */
  active: Destination | null
  onSelect: (d: Destination) => void
  onAccount: () => void
  accountActive: boolean
  /**
   * Your profile photo, when there is one.
   *
   * The desktop header shows it in the avatar; below 760px that header does not
   * exist, so without this the photo you just uploaded appears nowhere on the
   * device you are most likely to be looking at it on.
   */
  accountPhoto?: string
  /**
   * Unread messages, shown on Marketing.
   *
   * Always null today — the inbox is designed but cannot be built until the
   * Meta token carries `instagram_manage_messages`. Threaded through now
   * because a badge added later is a badge nobody notices has appeared.
   */
  marketingBadge?: number | null
}) {
  return (
    <nav className="tabbar" aria-label="Sections">
      {DESTINATIONS.map((d) => (
        <button
          key={d.key}
          className={`tabbar__item${active === d.key ? ' tabbar__item--active' : ''}`}
          onClick={() => onSelect(d.key)}
          aria-current={active === d.key ? 'page' : undefined}
        >
          <span className="tabbar__icon">
            {ICONS[d.key]}
            {d.key === 'marketing' && marketingBadge ? (
              <span className="tabbar__badge">{marketingBadge}</span>
            ) : null}
          </span>
          <span className="tabbar__label">{d.label}</span>
        </button>
      ))}

      <button
        className={`tabbar__item${accountActive ? ' tabbar__item--active' : ''}`}
        onClick={onAccount}
        aria-haspopup="dialog"
        aria-expanded={accountActive}
      >
        <span className="tabbar__icon">
          {accountPhoto ? (
            <img className="tabbar__photo" src={accountPhoto} alt="" />
          ) : (
            '👤'
          )}
        </span>
        <span className="tabbar__label">Account</span>
      </button>
    </nav>
  )
}
